import type { TableState, Updater } from '@tanstack/react-table';
import { isEqual, merge, omit, pick } from 'lodash-es';
import { useState } from 'react';

type SharedTableState = TableState<any>;
type PersistedTableState = Partial<SharedTableState>;

// State not stored in either localStorage or sessionStorage.
const blacklistProps = ['rowSelection'];

// Properties only preserved in session instead of localStorage.
const sessionOnlyProps = ['pagination'];

function getStorageState(persistId: string, storage: Storage) {
  const data = storage.getItem(persistId);
  return data ? JSON.parse(data) : {};
}

function getLocalStorageState(persistId: string) {
  return getStorageState(persistId, localStorage);
}
function getSessionStorageState(persistId: string) {
  return getStorageState(persistId, sessionStorage);
}

function saveLocalStorageState(
  persistId: string,
  nextState: PersistedTableState
) {
  const localStorageData = omit(nextState, sessionOnlyProps);
  localStorage.setItem(persistId, JSON.stringify(localStorageData));
}
function saveSessionStorageState(
  persistId: string,
  nextState: PersistedTableState
) {
  const sessionStorageData = pick(nextState, sessionOnlyProps);
  sessionStorage.setItem(persistId, JSON.stringify(sessionStorageData));
}

function getDefaultState(): PersistedTableState {
  return {
    columnFilters: [],
    columnOrder: [],
    columnVisibility: {},
    expanded: {},
    grouping: [],
    pagination: { pageIndex: 0, pageSize: 50 },
    rowSelection: {},
    sorting: [],
  };
}

function getPersistId(tableId: string | undefined) {
  if (tableId === undefined) {
    return undefined;
  }
  return ['table', tableId].join('.');
}

const getInitialState = (
  tableId: string | undefined,
  initialState: PersistedTableState
) => {
  const persistId = getPersistId(tableId);

  if (persistId === undefined) {
    return merge({}, getDefaultState(), initialState);
  }

  return merge(
    {},
    getDefaultState(),
    initialState,
    getLocalStorageState(persistId),
    getSessionStorageState(persistId)
  );
};

const save = (tableId: string | undefined, nextState: PersistedTableState) => {
  const persistId = getPersistId(tableId);

  const pruned = omit(nextState, blacklistProps);

  if (typeof persistId === 'string') {
    saveLocalStorageState(persistId, pruned);
    saveSessionStorageState(persistId, pruned);
  }
};

export const useTableState = (
  tableId: string | undefined,
  initialState: PersistedTableState = {}
) => {
  const [tableState, setTableState] = useState<PersistedTableState>(
    getInitialState(tableId, initialState)
  );

  const updateTableState = <TKey extends keyof SharedTableState>(
    key: TKey,
    updater: Updater<NonNullable<SharedTableState[TKey]>>
  ) => {
    setTableState((currentState) => {
      const currentValue =
        currentState[key] ??
        getDefaultState()[key as keyof PersistedTableState];
      const nextValue =
        typeof updater === 'function'
          ? (
              updater as (
                old: NonNullable<SharedTableState[TKey]>
              ) => NonNullable<SharedTableState[TKey]>
            )(currentValue as NonNullable<SharedTableState[TKey]>)
          : updater;
      if (isEqual(currentValue, nextValue)) {
        return currentState;
      }
      const nextState = {
        ...currentState,
        [key]: nextValue,
      };
      save(tableId, nextState);
      return nextState;
    });
  };

  return { tableState, updateTableState };
};
